import { useState } from 'react';
import { Building2, Lock, Bell, Users, ChevronRight, Lightbulb } from 'lucide-react';
import clsx from 'clsx';
import { GeneralSettings, NotificationSettings, SecuritySettings, TeamSettings, FeatureRequests } from './components';

const TABS = [
    { id: 'general', label: 'Şirket Bilgileri', description: 'Şirket ve iletişim bilgileri', icon: Building2, color: 'primary' },
    { id: 'team', label: 'Ekip Yönetimi', description: 'Kullanıcılar ve roller', icon: Users, color: 'warning' },
    { id: 'notifications', label: 'Bildirimler', description: 'Bildirim tercihleri', icon: Bell, color: 'tertiary' },
    { id: 'security', label: 'Güvenlik', description: 'Şifre ve 2FA', icon: Lock, color: 'error' },
    { id: 'requests', label: 'Geliştirme Talepleri', description: 'Özellik ve hata bildirimleri', icon: Lightbulb, color: 'success' },
];

export default function Settings() {
    const [activeTab, setActiveTab] = useState('general');

    const colorClasses = {
        primary: 'bg-primary/10 text-primary',
        warning: 'bg-warning/10 text-warning',
        tertiary: 'bg-tertiary/10 text-tertiary',
        error: 'bg-error/10 text-error',
        success: 'bg-success/10 text-success',
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'general':
                return <GeneralSettings />;
            case 'team':
                return <TeamSettings />;
            case 'notifications':
                return <NotificationSettings />;
            case 'security':
                return <SecuritySettings />;
            case 'requests':
                return <FeatureRequests />;
            default:
                return null;
        }
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-text-primary">Ayarlar</h1>
                <p className="text-text-tertiary mt-1">Uygulama ve şirket ayarlarını yönetin</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* Sidebar Navigation */}
                <div className="w-full lg:w-72 shrink-0">
                    <div className="bg-bg-secondary rounded-2xl border border-border-light overflow-hidden">
                        <div className="p-4 border-b border-border-light bg-gradient-to-r from-bg-tertiary/50 to-transparent">
                            <h3 className="font-semibold text-text-primary">Ayar Kategorileri</h3>
                        </div>
                        <nav className="p-2">
                            {TABS.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = activeTab === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={clsx(
                                            'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl mb-1 transition-all text-left cursor-pointer',
                                            isActive
                                                ? 'bg-primary text-white shadow-lg shadow-primary/20'
                                                : 'text-text-secondary hover:bg-bg-tertiary'
                                        )}
                                    >
                                        <div className={clsx(
                                            'w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors',
                                            isActive ? 'bg-white/20' : colorClasses[tab.color]
                                        )}>
                                            <Icon size={18} className={isActive ? 'text-white' : ''} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={clsx(
                                                'font-medium',
                                                isActive ? 'text-white' : 'text-text-primary'
                                            )}>
                                                {tab.label}
                                            </p>
                                            <p className={clsx(
                                                'text-xs truncate',
                                                isActive ? 'text-white/70' : 'text-text-muted'
                                            )}>
                                                {tab.description}
                                            </p>
                                        </div>
                                        <ChevronRight
                                            size={16}
                                            className={clsx(
                                                'shrink-0 transition-opacity',
                                                isActive ? 'text-white opacity-100' : 'text-text-muted opacity-0'
                                            )}
                                        />
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1">
                    <div className="bg-bg-secondary rounded-2xl border border-border-light p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
}
